import { useEffect } from 'react';
import './SparkAnimation.css';

export default function SparkAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    const battleBgRect = battleBg.getBoundingClientRect();

    // Calculate positions relative to battle background
    const startX = (playerRect.left + playerRect.width / 2) - battleBgRect.left;
    const startY = (playerRect.top + playerRect.height / 2) - battleBgRect.top;
    const endX = (opponentRect.left + opponentRect.width / 2) - battleBgRect.left;
    const endY = (opponentRect.top + opponentRect.height / 2) - battleBgRect.top;

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];

    // Player charging animation with electric buildup
    playerEl.classList.add('spark-charge');
    
    // Create small electric sparks around the player during charge
    for (let i = 0; i < 8; i++) {
      const chargeTimer = setTimeout(() => {
        const chargeSpark = document.createElement('div');
        chargeSpark.className = 'spark-charge-particle';
        
        // Random position around player
        const angle = Math.random() * Math.PI * 2;
        const distance = 15 + Math.random() * 25;
        chargeSpark.style.left = `${startX + Math.cos(angle) * distance}px`;
        chargeSpark.style.top = `${startY + Math.sin(angle) * distance}px`;
        
        battleBg.appendChild(chargeSpark);
        elements.push(chargeSpark);
        
        // Remove charge spark after animation
        const sparkRemoveTimer = setTimeout(() => {
          if (chargeSpark.isConnected) {
            chargeSpark.remove();
          }
        }, 400);
        timers.push(sparkRemoveTimer);
      }, i * 80);
      timers.push(chargeTimer);
    }
    
    // Create multiple spark bolts that travel from player to opponent
    const sparkAttackTimer = setTimeout(() => {
      playerEl.classList.remove('spark-charge');
      
      // Create 3-4 spark bolts with slight delays for a crackling effect
      for (let i = 0; i < 4; i++) {
        const sparkTimer = setTimeout(() => {
          const sparkBolt = document.createElement('div');
          sparkBolt.className = 'spark-bolt';
          
          // Calculate path from player to opponent with slight randomization
          const offsetX = (Math.random() - 0.5) * 20;
          const offsetY = (Math.random() - 0.5) * 20;
          const targetX = endX + offsetX;
          const targetY = endY + offsetY;
          
          // Position spark bolt
          sparkBolt.style.left = `${startX}px`;
          sparkBolt.style.top = `${startY}px`;
          sparkBolt.style.setProperty('--target-x', `${targetX - startX}px`);
          sparkBolt.style.setProperty('--target-y', `${targetY - startY}px`);
          
          battleBg.appendChild(sparkBolt);
          elements.push(sparkBolt);
          
          // Create impact effect when spark hits
          const impactTimer = setTimeout(() => {
            // Create impact sparks at opponent
            for (let j = 0; j < 6; j++) {
              const impactSpark = document.createElement('div');
              impactSpark.className = 'spark-impact';
              
              const impactAngle = Math.random() * Math.PI * 2;
              const impactDistance = 10 + Math.random() * 15;
              impactSpark.style.left = `${targetX + Math.cos(impactAngle) * impactDistance}px`;
              impactSpark.style.top = `${targetY + Math.sin(impactAngle) * impactDistance}px`;
              
              // Set random direction for impact spark
              const impactDx = Math.cos(impactAngle) * (20 + Math.random() * 15);
              const impactDy = Math.sin(impactAngle) * (20 + Math.random() * 15);
              impactSpark.style.setProperty('--impact-dx', `${impactDx}px`);
              impactSpark.style.setProperty('--impact-dy', `${impactDy}px`);
              
              battleBg.appendChild(impactSpark);
              elements.push(impactSpark);
            }
            
            // Opponent hit effect
            opponentEl.classList.add('spark-hit');
            const hitRemoveTimer = setTimeout(() => {
              opponentEl.classList.remove('spark-hit');
            }, 300);
            timers.push(hitRemoveTimer);
            
          }, 200); // Impact happens 200ms after spark starts
          timers.push(impactTimer);
          
        }, i * 150); // Stagger the spark bolts
        timers.push(sparkTimer);
      }
      
      // Create brief electric flash effect
      const flash = document.createElement('div');
      flash.className = 'spark-flash';
      flash.style.left = `${endX}px`;
      flash.style.top = `${endY}px`;
      battleBg.appendChild(flash);
      elements.push(flash);
      
    }, 600); // Start spark attack after charge
    timers.push(sparkAttackTimer);
    
    // Cleanup and complete animation
    const cleanupTimer = setTimeout(() => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Remove all elements
      elements.forEach(element => {
        if (element.isConnected) {
          element.remove();
        }
      });
      
      // Remove any remaining classes
      playerEl.classList.remove('spark-charge');
      opponentEl.classList.remove('spark-hit');
      
      onComplete();
    }, 1800);
    timers.push(cleanupTimer);

    // Cleanup function for component unmount
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      elements.forEach(element => {
        if (element.isConnected) {
          element.remove();
        }
      });
      playerEl.classList.remove('spark-charge');
      opponentEl.classList.remove('spark-hit');
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SparkAnimation.moveType = "electric";
